import { Badge, Button, Dropdown, FormInput, FormSelect } from '@/components/UI';
import { FaCheck, FaExternalLinkAlt } from 'react-icons/fa';
import type {
    ExternalAuditApproval,
    ExternalAuditSessionQueryResponse,
    ExternalAuditTool,
    McpClientReadiness,
    OptionTimingEvidenceReadiness,
    OptimizerJobRow,
    ScreenerOptimizerAiAnalysisObjective
} from './types';
import {
    analysisObjectiveOptions,
    developerAccessPath,
    externalAiAuditCandidateLimit,
    externalAiAuditRobustLimit,
    externalAuditToolOptions,
    externalToolLaunchUrls
} from './constants';
import {
    badgeVariantForMcpStatus,
    buildExternalAuditReadinessSetupText,
    labelForExternalAuditTool,
    optionTimingEvidenceBadgeState,
    walkForwardBadgeState
} from './helpers';

interface ExternalAuditSectionProps {
    selectedMcpReadiness: McpClientReadiness | undefined;
    externalAuditTool: ExternalAuditTool;
    setExternalAuditTool: (tool: ExternalAuditTool) => void;
    analysisObjective: ScreenerOptimizerAiAnalysisObjective;
    setAnalysisObjective: (objective: ScreenerOptimizerAiAnalysisObjective) => void;
    userGuidance: string;
    setUserGuidance: (guidance: string) => void;
    targetSignalRetentionPct: string;
    setTargetSignalRetentionPct: (target: string) => void;
    targetSignalRetentionTolerancePct: string;
    setTargetSignalRetentionTolerancePct: (tolerance: string) => void;
    selectedJobUsesOptionMetric: boolean;
    selectedJobUsesOptionEv: boolean;
    selectedJob: OptimizerJobRow;
    optionTimingEvidenceReadiness: OptionTimingEvidenceReadiness | null;
    optionTimingEvidenceLoading: boolean;
    activeExternalWorkbench: ExternalAuditSessionQueryResponse['workbench'] | null;
    activeExternalAnalysisRun: ExternalAuditSessionQueryResponse['analysisRun'] | null;
    activeExternalAnalysisPlan: ExternalAuditSessionQueryResponse['analysisPlan'] | null;
    activeExternalRecommendations: ExternalAuditSessionQueryResponse['recommendations'];
    walkForwardEvaluations: ExternalAuditSessionQueryResponse['walkForwardEvaluations'];
    experimentWalkForwardExposure: ExternalAuditSessionQueryResponse['experimentWalkForwardExposure'];
    activeExternalPrompt: string;
    externalApprovalByArtifactId: Map<number, ExternalAuditApproval>;
    externalAuditSessionPending: boolean;
    onStartExternalAuditSession: () => void;
    onCopyText: (text: string, label: string) => void;
    onOpenExternalAuditTool: () => void;
    decidePromotionApprovalPending: boolean;
    onDecidePromotionApproval: (input: { approvalId: number; status: 'approved' | 'rejected' | 'cancelled' }) => void;
    jobRequestApprovalByArtifactId: Map<number, ExternalAuditApproval>;
    startApprovedOptimizerJobPending: boolean;
    onStartApprovedOptimizerJob: (artifactId: number) => void;
}

const ExternalAuditSection = ({
    selectedMcpReadiness,
    externalAuditTool,
    setExternalAuditTool,
    analysisObjective,
    setAnalysisObjective,
    userGuidance,
    setUserGuidance,
    targetSignalRetentionPct,
    setTargetSignalRetentionPct,
    targetSignalRetentionTolerancePct,
    setTargetSignalRetentionTolerancePct,
    selectedJobUsesOptionMetric,
    selectedJobUsesOptionEv,
    selectedJob,
    optionTimingEvidenceReadiness,
    optionTimingEvidenceLoading,
    activeExternalWorkbench,
    activeExternalAnalysisRun,
    activeExternalAnalysisPlan,
    activeExternalRecommendations,
    walkForwardEvaluations,
    experimentWalkForwardExposure,
    activeExternalPrompt,
    externalApprovalByArtifactId,
    externalAuditSessionPending,
    onStartExternalAuditSession,
    onCopyText,
    onOpenExternalAuditTool,
    decidePromotionApprovalPending,
    onDecidePromotionApproval,
    jobRequestApprovalByArtifactId,
    startApprovedOptimizerJobPending,
    onStartApprovedOptimizerJob
}: ExternalAuditSectionProps) => {
    const jobRequestArtifacts = (activeExternalWorkbench?.artifacts ?? []).filter(
        (artifact) => artifact.artifactType === 'screener_optimizer_job_request'
    );

    const visibleObjectiveOptions = analysisObjectiveOptions.filter(
        (option) =>
            (!option.optionOnly || selectedJobUsesOptionMetric) &&
            (!option.nonOptionOnly || !selectedJobUsesOptionMetric) &&
            (!option.requiresOptionEvPerSignal || selectedJobUsesOptionEv)
    );
    const selectedObjective = analysisObjectiveOptions.find((option) => option.value === analysisObjective);
    const selectedObjectiveLabel = selectedObjective?.label ?? analysisObjective;
    const objectiveJobNote =
        analysisObjective === 'total_profit' && selectedJobUsesOptionEv
            ? 'This analysis reranks the total-profit candidates retained by an EV-per-signal optimizer job; it does not change the completed job’s original selection score.'
            : null;
    const hasWeakWalkForwardResult = activeExternalRecommendations.some(
        (recommendation) =>
            recommendation.walkForwardVerdictStatus === 'fail' ||
            recommendation.walkForwardVerdictStatus === 'inconclusive' ||
            recommendation.terminalReason !== null
    );
    const experimentWalkForwardRevealed = experimentWalkForwardExposure !== null;
    const timingEvidenceBadge = optionTimingEvidenceBadgeState(optionTimingEvidenceReadiness, optionTimingEvidenceLoading);

    const walkForward = walkForwardBadgeState(selectedJob);
    const walkForwardReady = selectedJob.validationWindowStage === 'ready';
    const analysisQueued =
        activeExternalAnalysisRun !== null &&
        ['waiting_for_job', 'queued', 'session_created', 'running', 'waiting_for_confirmation'].includes(activeExternalAnalysisRun.status);

    return (
        <div className="border border-zinc-800 bg-zinc-950/40 rounded-lg px-3 py-2 mb-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-white text-sm font-semibold">AI screener recommendation</span>
                        {selectedJobUsesOptionMetric && (
                            <Badge title={timingEvidenceBadge.message} variant={timingEvidenceBadge.variant}>
                                {timingEvidenceBadge.label}
                            </Badge>
                        )}
                        <Badge title={walkForward.detail} variant={walkForward.variant}>
                            Walk-forward {walkForward.label.toLowerCase()}
                        </Badge>
                        <Badge variant={badgeVariantForMcpStatus(selectedMcpReadiness?.status)}>
                            {selectedMcpReadiness?.label ?? 'MCP not checked'}
                        </Badge>
                        {activeExternalWorkbench?.session && (
                            <Badge variant="secondary">{activeExternalWorkbench.session.status.replaceAll('_', ' ')}</Badge>
                        )}
                        <Badge variant="secondary">{externalAiAuditCandidateLimit} candidates</Badge>
                        <Badge variant="secondary">{externalAiAuditRobustLimit} robust checks</Badge>
                        <Badge variant="secondary">{selectedObjectiveLabel} objective</Badge>
                        {activeExternalAnalysisPlan && (
                            <Badge variant={activeExternalAnalysisPlan.completion.pendingRequiredCount > 0 ? 'warning' : 'success'}>
                                plan {activeExternalAnalysisPlan.completion.completedRequiredCount}/
                                {activeExternalAnalysisPlan.completion.requiredCount}
                            </Badge>
                        )}
                    </div>
                    <div className="text-zinc-500 text-xs mt-1 leading-5">
                        {selectedMcpReadiness?.detail ?? `${labelForExternalAuditTool(externalAuditTool)} setup has not been checked yet.`}
                    </div>
                    {selectedMcpReadiness?.matchedToken && (
                        <div className="text-zinc-600 text-xs mt-1">
                            Token {selectedMcpReadiness.matchedToken.tokenPrefix} · last used{' '}
                            {selectedMcpReadiness.matchedToken.lastUsedDateTime ?? 'never'}
                        </div>
                    )}
                    {selectedMcpReadiness?.matchedOAuthConnection && (
                        <div className="text-zinc-600 text-xs mt-1">
                            OAuth {selectedMcpReadiness.matchedOAuthConnection.clientName} · last used{' '}
                            {selectedMcpReadiness.matchedOAuthConnection.lastUsedAt ?? 'not yet'}
                        </div>
                    )}
                    {activeExternalWorkbench?.session && (
                        <div className="text-zinc-500 text-xs mt-2">
                            Workbench session #{activeExternalWorkbench.session.id}:{' '}
                            {activeExternalWorkbench.session.firstAgentSeenAt
                                ? `agent connected ${activeExternalWorkbench.session.firstAgentSeenAt}`
                                : 'waiting for an external MCP agent'}
                        </div>
                    )}
                    {(activeExternalWorkbench?.artifacts.length ?? 0) > 0 && (
                        <div className="text-zinc-500 text-xs mt-2">
                            Artifacts:{' '}
                            {activeExternalWorkbench?.artifacts.map((artifact) => `${artifact.name} (${artifact.status})`).join(', ')}
                        </div>
                    )}
                    <div className="mt-2 max-w-3xl text-xs leading-5 text-zinc-400">{selectedObjective?.description}</div>
                    {objectiveJobNote && <div className="mt-1 max-w-3xl text-xs leading-5 text-amber-300">{objectiveJobNote}</div>}
                    {selectedJobUsesOptionMetric && (
                        <div className="mt-2 max-w-3xl rounded border border-zinc-800 bg-zinc-950/60 px-2 py-1.5 text-xs leading-5 text-zinc-500">
                            {timingEvidenceBadge.message}
                        </div>
                    )}
                    {!walkForwardReady && (
                        <div
                            className={`mt-2 max-w-3xl rounded border px-2 py-1.5 text-xs leading-5 ${
                                selectedJob.validationWindowStage === 'failed'
                                    ? 'border-red-500/30 bg-red-500/10 text-red-200'
                                    : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200'
                            }`}
                        >
                            Training is complete, but the walk-forward dataset is {walkForward.label.toLowerCase()}. {walkForward.detail}.
                            AI screener generation is unavailable until both immutable windows are ready.
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                    <Dropdown
                        closeOnItemClick
                        buttonClassName="border border-zinc-700 bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
                        buttonContent={selectedObjectiveLabel}
                        menuClassName="w-80"
                        position="right"
                    >
                        <div className="px-3 py-2 text-[11px] leading-snug text-zinc-500 border-b border-zinc-800">
                            What the AI analysis optimizes for when ranking candidate rules.
                        </div>
                        {visibleObjectiveOptions.map(({ value, label, description }) => {
                            const isSelected = value === analysisObjective;

                            return (
                                <button
                                    aria-checked={isSelected}
                                    className="w-full px-3 py-2 text-left hover:bg-zinc-800 transition-colors"
                                    key={value}
                                    role="menuitemradio"
                                    type="button"
                                    onClick={() => setAnalysisObjective(value)}
                                >
                                    <div className="flex items-center gap-2 text-sm text-zinc-200">
                                        {label}
                                        {isSelected && <FaCheck aria-hidden className="text-cyan-400" size={10} />}
                                    </div>
                                    <div className="text-[11px] leading-snug text-zinc-500">{description}</div>
                                </button>
                            );
                        })}
                    </Dropdown>
                    <FormSelect
                        noMargin
                        aria-label="External agent"
                        fullWidth={false}
                        id="opt-external-audit-tool"
                        options={externalAuditToolOptions}
                        size="sm"
                        value={externalAuditTool}
                        onChange={(e) => setExternalAuditTool(e.target.value as ExternalAuditTool)}
                    />
                    <Button
                        disabled={externalAuditSessionPending || analysisQueued || !walkForwardReady}
                        size="sm"
                        variant="primary"
                        onClick={onStartExternalAuditSession}
                    >
                        {externalAuditSessionPending
                            ? 'Creating...'
                            : analysisQueued
                              ? 'Recommendation queued'
                              : activeExternalAnalysisRun
                                ? 'Generate Another Recommendation'
                                : 'Generate Recommendation'}
                    </Button>
                    {activeExternalPrompt && (
                        <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => onCopyText(activeExternalPrompt, 'AI screener recommendation prompt')}
                        >
                            Copy Prompt
                        </Button>
                    )}
                    {externalToolLaunchUrls[externalAuditTool] && (
                        <Button icon={<FaExternalLinkAlt />} size="sm" variant="secondary" onClick={onOpenExternalAuditTool}>
                            Open {labelForExternalAuditTool(externalAuditTool)}
                        </Button>
                    )}
                    <Button size="sm" to={developerAccessPath} variant="secondary">
                        Developer Access
                    </Button>
                </div>
            </div>
            <div className="mt-3 rounded border border-zinc-800 bg-black/15 p-3">
                <label className="mb-1 block text-sm font-medium text-zinc-300" htmlFor="opt-user-guidance">
                    Guidance for this screener
                </label>
                <textarea
                    className="min-h-20 w-full resize-y rounded border border-zinc-800 bg-zinc-950 p-2 text-xs leading-5 text-zinc-200"
                    id="opt-user-guidance"
                    maxLength={2000}
                    placeholder="For example: make this materially different from our other variants; favor recent selloffs that have stabilized; preserve average-profit lift while improving win rate."
                    value={userGuidance}
                    onChange={(event) => setUserGuidance(event.target.value)}
                />
                <div className="mt-1 text-xs text-zinc-600">
                    Optional, at least 20 characters. Related strategy-variant screeners are included automatically so the agent can avoid
                    repeating them. Recommendations create inactive research variants for backtesting; trading requires separate approval.
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <FormInput
                        helpText="Optional share of optimizer evaluation signals to keep—not executed option trades."
                        id="opt-target-signal-retention"
                        label="Target signal retention"
                        max={100}
                        min={1}
                        noMargin
                        placeholder="50"
                        step={1}
                        suffix="%"
                        type="number"
                        value={targetSignalRetentionPct}
                        onChange={(event) => setTargetSignalRetentionPct(event.target.value)}
                    />
                    <FormInput
                        disabled={!targetSignalRetentionPct.trim()}
                        helpText="Allowed range around the target, in percentage points."
                        id="opt-target-signal-retention-tolerance"
                        label="Tolerance"
                        max={50}
                        min={0}
                        noMargin
                        step={1}
                        suffix="± %"
                        type="number"
                        value={targetSignalRetentionTolerancePct}
                        onChange={(event) => setTargetSignalRetentionTolerancePct(event.target.value)}
                    />
                </div>
            </div>
            {selectedMcpReadiness?.status !== 'connected' && (
                <pre className="mt-3 whitespace-pre-wrap break-words rounded border border-zinc-800 bg-black/20 p-2 text-xs leading-5 text-zinc-500">
                    {buildExternalAuditReadinessSetupText({
                        readiness: selectedMcpReadiness,
                        tool: externalAuditTool
                    })}
                </pre>
            )}
            {activeExternalPrompt && (
                <textarea
                    readOnly
                    className="mt-3 h-36 w-full resize-y rounded border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs text-zinc-200"
                    value={activeExternalPrompt}
                />
            )}
            {hasWeakWalkForwardResult && (
                <div className="mt-3 rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs leading-5 text-red-200">
                    A recommendation has failed or inconclusive walk-forward performance. A completed evaluation can still support an
                    inactive research variant for backtesting. Its performance verdict remains unchanged.
                </div>
            )}
            {experimentWalkForwardRevealed && (
                <div className="mt-3 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs leading-5 text-amber-200">
                    This experiment’s walk-forward window was already evaluated by optimizer job {experimentWalkForwardExposure.jobId}{' '}
                    (artifact {experimentWalkForwardExposure.artifactId}). You can generate another recommendation, but its walk-forward
                    metrics will be reused/adaptive and exploratory. You can still create an inactive research variant and compare it with
                    its parent in backtests.
                </div>
            )}
            {activeExternalRecommendations.length > 0 && (
                <div className="mt-3 rounded border border-zinc-800 bg-zinc-950/60 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-zinc-300">AI Recommendations</div>
                        <Badge variant="secondary">{activeExternalRecommendations.length}</Badge>
                    </div>
                    <div className="space-y-2">
                        {activeExternalRecommendations.map((recommendation) => {
                            const approval = externalApprovalByArtifactId.get(recommendation.artifactId);
                            const hasCandidateLineage =
                                recommendation.resultIds.length > 0 || recommendation.candidateEvaluationId !== null;
                            const recommendationWalkForward =
                                walkForwardEvaluations.find((evaluation) => evaluation.artifactId === recommendation.artifactId) ?? null;
                            const terminalReason = recommendation.terminalReason;
                            const isApplied = recommendation.status === 'applied' || approval?.status === 'executed';
                            const recommendationValidationFailed =
                                recommendation.walkForwardVerdictStatus === 'fail' ||
                                recommendation.walkForwardVerdictStatus === 'inconclusive' ||
                                terminalReason !== null;
                            const usesReusedEvidence = recommendation.walkForwardEvidenceKind === 'reused_adaptive';
                            const recommendationStatusLabel = isApplied
                                ? 'research variant created'
                                : recommendation.walkForwardVerdictStatus === 'fail' || terminalReason === 'walk_forward_fail'
                                  ? 'walk-forward failed'
                                  : recommendation.walkForwardVerdictStatus === 'inconclusive' ||
                                      terminalReason === 'walk_forward_inconclusive'
                                    ? 'walk-forward inconclusive'
                                    : recommendationWalkForward?.status === 'failed'
                                      ? 'walk-forward retry needed'
                                      : recommendationWalkForward?.status === 'pending'
                                        ? 'walk-forward pending'
                                        : recommendation.status === 'working'
                                          ? 'legacy draft'
                                          : recommendation.status === 'submitted'
                                            ? 'walk-forward pending'
                                            : recommendation.status === 'recommended'
                                              ? 'research variant pending'
                                              : recommendation.status;
                            const recommendationStatusVariant = recommendationValidationFailed
                                ? 'danger'
                                : recommendation.status === 'working' || recommendation.status === 'submitted'
                                  ? 'warning'
                                  : usesReusedEvidence
                                    ? 'warning'
                                    : recommendation.status === 'recommended' || recommendation.status === 'applied'
                                      ? 'success'
                                      : 'secondary';

                            return (
                                <div className="rounded border border-zinc-800 bg-zinc-950/70 p-2 text-xs" key={recommendation.artifactId}>
                                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                        <div className="min-w-0 font-medium text-white">{recommendation.name}</div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant={recommendationStatusVariant}>{recommendationStatusLabel}</Badge>
                                            {approval && <Badge variant="secondary">{approval.status.replaceAll('_', ' ')}</Badge>}
                                            {!isApplied && !hasCandidateLineage && <Badge variant="warning">not promotable</Badge>}
                                            {usesReusedEvidence && <Badge variant="warning">reused/adaptive evidence</Badge>}
                                        </div>
                                    </div>
                                    <div className="text-zinc-500">
                                        {recommendation.recommendationKind === 'or_bundle'
                                            ? 'OR bundle'
                                            : recommendation.recommendationKind === 'generated_rule'
                                              ? 'Generated rule'
                                              : recommendation.recommendationKind === 'simplified_rule'
                                                ? 'Simplified rule'
                                                : 'Single result'}{' '}
                                        ·{' '}
                                        {recommendation.candidateEvaluationId !== null
                                            ? `candidateEvaluationId=${recommendation.candidateEvaluationId}`
                                            : `resultIds=[${recommendation.resultIds.length > 0 ? recommendation.resultIds.join(', ') : 'missing'}]`}
                                    </div>
                                    <div className="mt-1 leading-5 text-zinc-400">{recommendation.summary}</div>
                                    {recommendation.userBriefAssessment && (
                                        <div className="mt-2 rounded border border-zinc-800 bg-black/20 px-2 py-1.5 leading-5 text-zinc-400">
                                            <span className="font-medium text-zinc-300">Brief fit:</span>{' '}
                                            {recommendation.userBriefAssessment}
                                        </div>
                                    )}
                                    {recommendation.validationSummary && (
                                        <div className="mt-1 whitespace-pre-line leading-5 text-zinc-500">
                                            {recommendation.validationSummary}
                                        </div>
                                    )}
                                    {recommendationWalkForward?.status === 'ready' && (
                                        <div
                                            className={`mt-2 rounded border px-2 py-1.5 leading-5 ${
                                                recommendationValidationFailed || usesReusedEvidence
                                                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                                                    : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                                            }`}
                                        >
                                            {recommendation.walkForwardEvidenceKind === 'reused_adaptive'
                                                ? 'Exploratory reused-data walk-forward'
                                                : 'Fresh sealed walk-forward'}{' '}
                                            computed: {recommendationWalkForward.passingCount?.toLocaleString() ?? '0'} of{' '}
                                            {recommendationWalkForward.totalCount?.toLocaleString() ?? '0'} signals passed; lift{' '}
                                            {recommendationWalkForward.lift?.toFixed(2) ?? 'n/a'} across{' '}
                                            {recommendationWalkForward.uniqueEntities?.toLocaleString() ?? '0'} entities.
                                            {recommendation.walkForwardVerdictStatus && (
                                                <div className="mt-1">
                                                    Verdict {recommendation.walkForwardVerdictStatus.toUpperCase()}
                                                    {recommendation.walkForwardVerdictReason
                                                        ? ` — ${recommendation.walkForwardVerdictReason}`
                                                        : '.'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {!isApplied && !hasCandidateLineage && (
                                        <div className="mt-2 rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 leading-5 text-amber-200">
                                            This recommendation cannot create a screener because it has no persisted optimizer candidate
                                            lineage.
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {jobRequestArtifacts.length > 0 && (
                <div className="mt-3 rounded border border-zinc-800 bg-zinc-950/60 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="text-sm font-medium text-zinc-300">Proposed follow-up optimizer jobs</div>
                        <Badge variant="secondary">{jobRequestArtifacts.length}</Badge>
                    </div>
                    <div className="space-y-2">
                        {jobRequestArtifacts.map((artifact) => {
                            const approval = jobRequestApprovalByArtifactId.get(artifact.id);
                            const canDecide = approval?.status === 'requested';
                            const canStart = approval?.status === 'approved' && artifact.status !== 'applied';
                            const startedJobId = artifact.sourceTable === 'screener_optimizer_jobs' ? artifact.sourceId : null;

                            return (
                                <div className="rounded border border-zinc-800 bg-zinc-950/70 p-2 text-xs" key={artifact.id}>
                                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                                        <div className="min-w-0 font-medium text-white">{artifact.name}</div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant="secondary">{artifact.status}</Badge>
                                            {approval && <Badge variant="secondary">{approval.status.replaceAll('_', ' ')}</Badge>}
                                        </div>
                                    </div>
                                    {artifact.validationSummary && (
                                        <div className="leading-5 text-zinc-400">{artifact.validationSummary}</div>
                                    )}
                                    {startedJobId !== null && (
                                        <div className="mt-1 text-emerald-300">Started optimizer job {startedJobId}.</div>
                                    )}
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {canDecide && approval && (
                                            <>
                                                <Button
                                                    disabled={decidePromotionApprovalPending}
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() =>
                                                        onDecidePromotionApproval({ approvalId: approval.id, status: 'approved' })
                                                    }
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    disabled={decidePromotionApprovalPending}
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() =>
                                                        onDecidePromotionApproval({ approvalId: approval.id, status: 'rejected' })
                                                    }
                                                >
                                                    Reject
                                                </Button>
                                            </>
                                        )}
                                        {canStart && (
                                            <Button
                                                disabled={startApprovedOptimizerJobPending}
                                                size="sm"
                                                variant="primary"
                                                onClick={() => onStartApprovedOptimizerJob(artifact.id)}
                                            >
                                                {startApprovedOptimizerJobPending ? 'Starting...' : 'Start job'}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExternalAuditSection;
